import type { AdaptationSuggestion } from '../../application/dto/AdaptationSuggestion';
import { LayersIcon } from '../icons';
import { Button } from './Button';
import { resolveImagery } from '../design-system/data/imagery';
import { useTranslation } from '../design-system/data/translations';

export type AdaptiveGuidanceDecision = 'accepted' | 'rejected' | null;

/** Pack V2 (1870×841) — remplace le bandeau vectoriel `guidance.svg` ; celui-ci reste défini en
 * CSS pour rollback (voir revue visuelle « intégration finale des 20 visuels »). */
const CARD_IMAGE = resolveImagery('card-guidance-v2.png');
const CARD_IMAGE_POSITION = '50% 50%';

const CARD_BANNER = (
  <div className="thematic-banner thematic-banner--photo" aria-hidden="true">
    <img
      src={CARD_IMAGE}
      alt=""
      className="thematic-banner__image"
      style={{ objectPosition: CARD_IMAGE_POSITION }}
    />
    <div className="thematic-banner__veil" />
  </div>
);

interface AdaptiveGuidanceBannerProps {
  /** `undefined` : pas de proposition en cours — voir `decision` pour l'état post-décision. */
  readonly suggestion: AdaptationSuggestion | undefined;
  /** Décision déjà prise pour ce cycle — distincte de `suggestion.decisionType`, qui change
   * après l'appel API et ferait sinon disparaître la carte (voir revue visuelle V7 #6). */
  readonly decision: AdaptiveGuidanceDecision;
  readonly busy: boolean;
  readonly onAccept: () => void;
  readonly onReject: () => void;
}

/**
 * Adaptive Guidance System — Phase 1. L'emplacement Guidance adaptative reste toujours rendu :
 * avant décision, il montre la proposition et ses actions ; après « Accepter »/« Garder l'ordre
 * actuel », il évolue vers un état confirmé au lieu de disparaître ; sans proposition, un état
 * neutre factuel. Accepting applies the already-computed proposed order through the existing
 * reorder mechanism; rejecting keeps the current order untouched. The existing "Réorganiser"
 * control remains available either way, as the user's own alternative ("remplacer") to any
 * adaptation this banner proposes.
 */
export function AdaptiveGuidanceBanner({
  suggestion,
  decision,
  busy,
  onAccept,
  onReject,
}: AdaptiveGuidanceBannerProps) {
  const { t } = useTranslation();
  return (
    <section className="ags-banner editorial-card" aria-label={t('guidance.label')}>
      {CARD_BANNER}
      <div className="ags-banner__body">
        <p className="eyebrow">{t('guidance.label')}</p>
        {decision === 'accepted' ? (
          <p className="ags-banner__summary">
            <LayersIcon className="ags-banner__icon" aria-hidden="true" />
            {t('guidance.accepted')}
          </p>
        ) : decision === 'rejected' ? (
          <p className="ags-banner__summary">
            <LayersIcon className="ags-banner__icon" aria-hidden="true" />
            {t('guidance.rejected')}
          </p>
        ) : suggestion?.decisionType === 'propose' ? (
          <>
            <p className="ags-banner__summary">
              <LayersIcon className="ags-banner__icon" aria-hidden="true" />
              {t(`guidance.summary.${suggestion.guidanceCode}`)}
            </p>
            {suggestion.ruleId ? (
              <p className="ags-banner__reason">
                {t('guidance.reason.with_rule')
                  .replace('{mode}', t(`mode.${suggestion.modeKey}`))
                  .replace('{rule}', t(`guidance.rule.${suggestion.ruleId}`))}
              </p>
            ) : (
              <p className="ags-banner__reason">{t(`guidance.reason.${suggestion.guidanceCode}`)}</p>
            )}
            <div className="ags-banner__actions">
              <Button
                onClick={onAccept}
                disabled={busy}
                aria-label={t('guidance.accept.aria')}
              >
                {t('guidance.accept')}
              </Button>
              <Button
                variant="quiet"
                onClick={onReject}
                disabled={busy}
                aria-label={t('guidance.reject.aria')}
              >
                {t('guidance.reject')}
              </Button>
            </div>
          </>
        ) : (
          <p className="ags-banner__summary">{t('guidance.empty')}</p>
        )}
      </div>
    </section>
  );
}
